import React, { useState, useRef, useEffect, useCallback } from 'react';
import { SpinnerDotted } from 'spinners-react';
import styles from '../CreateRoute.module.scss';
import { getStopNumber } from '../constants/stopLabels';
import { formatCoordinates, parseCoordinatePair } from '../../../../utils/coordinates';

const MAPBOX_TOKEN =
  process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ||
  (typeof window !== 'undefined' && window.__env__?.REACT_APP_MAPBOX_ACCESS_TOKEN) ||
  '';

const formatDistance = (km) => {
  if (km === 0) return '—';
  if (km < 1) return `${(km * 1000).toFixed(0)} m`;
  if (km < 100) return `${km.toFixed(2)} km`;
  return `${(km / 1.852).toFixed(1)} nm`;
};

/** Geocoding fetch — returns Mapbox features */
const geocode = async (query, token) => {
  if (!token || !query.trim()) return [];
  try {
    const enc = encodeURIComponent(query.trim());
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${enc}.json?access_token=${token}&limit=5&types=place,locality,neighborhood,address,poi,region,country`;
    const res = await fetch(url);
    if (!res.ok) return [];
    return (await res.json()).features || [];
  } catch (_) {
    return [];
  }
};

/** Reverse geocode — returns place name for [lng, lat] */
const reverseGeocode = async (coord, token) => {
  if (!token || !coord) return null;
  try {
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${coord[0]},${coord[1]}.json?access_token=${token}&limit=1`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const feature = (await res.json()).features?.[0];
    return feature?.text || feature?.place_name || null;
  } catch (_) {
    return null;
  }
};

// ─── Lat/Lng coordinate parser ────────────────────────────────────────────────
const formatCoordLabel = (coord) => formatCoordinates(coord);

// ─── Start / Destination search field ────────────────────────────────────────
const LocationSearch = ({ color, letter, placeholder, onSelect, onPreview, syncCoord }) => {
  const [value, setValue]      = useState('');
  const [suggestions, setSugg] = useState([]);
  const [open, setOpen]        = useState(false);
  const [loading, setLoading]  = useState(false);
  const [confirmedCoord, setConfirmedCoord] = useState(null);
  const wrapRef                = useRef(null);
  const inputRef               = useRef(null);
  const debounceRef            = useRef(null);
  const skipRef                = useRef(false);
  const lastSyncedRef          = useRef(null);
  const confirmedNameRef         = useRef('');
  // coord being previewed while typing (not yet confirmed)
  const [previewCoord, setPreviewCoord] = useState(null);

  const applyLocation = useCallback((coord, name, notifyParent = true) => {
    skipRef.current = true;
    setValue(name);
    setPreviewCoord(null);
    setConfirmedCoord(coord);
    confirmedNameRef.current = name;
    lastSyncedRef.current = `${coord[0]},${coord[1]}`;
    if (notifyParent) onSelect(coord, name);
  }, [onSelect]);

  const resolvePlaceName = useCallback(async (coord) => {
    const name = await reverseGeocode(coord, MAPBOX_TOKEN);
    const key = `${coord[0]},${coord[1]}`;
    if (lastSyncedRef.current !== key) return;
    applyLocation(coord, name || formatCoordLabel(coord), false);
  }, [applyLocation]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!syncCoord) {
      lastSyncedRef.current = null;
      return;
    }
    if (inputRef.current && document.activeElement === inputRef.current) return;
    const key = `${syncCoord[0]},${syncCoord[1]}`;
    if (lastSyncedRef.current === key) return;
    lastSyncedRef.current = key;
    setPreviewCoord(null);
    setConfirmedCoord(syncCoord);
    resolvePlaceName(syncCoord);
  }, [syncCoord, resolvePlaceName]);

  useEffect(() => {
    if (skipRef.current) { skipRef.current = false; return undefined; }
    const trimmed = value.trim();
    if (!trimmed) {
      setSugg([]);
      setOpen(false);
      setPreviewCoord(null);
      setConfirmedCoord(null);
      confirmedNameRef.current = '';
      lastSyncedRef.current = null;
      onSelect(null, '');
      return undefined;
    }

    if (confirmedCoord && trimmed !== confirmedNameRef.current) {
      setConfirmedCoord(null);
      lastSyncedRef.current = null;
    }

    if (confirmedCoord && trimmed === confirmedNameRef.current) {
      return undefined;
    }

    // ── Direct lat/lng entry — resolve place name, show coords below ──────
    const parsed = parseCoordinatePair(trimmed);
    if (parsed) {
      clearTimeout(debounceRef.current);
      setSugg([]);
      setOpen(false);
      applyLocation(parsed, trimmed);
      resolvePlaceName(parsed);
      return undefined;
    }

    // ── Place-name search — preview top result on map while typing ────────
    if (trimmed.length < 2) { setSugg([]); setOpen(false); return undefined; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      const results = await geocode(trimmed, MAPBOX_TOKEN);
      setSugg(results);
      setOpen(results.length > 0);
      setLoading(false);
      if (results.length > 0) {
        setPreviewCoord(results[0].center);
        onPreview?.(results[0].center);
      }
    }, 320);
    return () => clearTimeout(debounceRef.current);
  }, [value, applyLocation, resolvePlaceName, onSelect, onPreview]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = useCallback((feature) => {
    const [lng, lat] = feature.center;
    const name = feature.text || feature.place_name || '';
    applyLocation([lng, lat], name);
    setSugg([]);
    setOpen(false);
  }, [applyLocation]);

  const handleConfirmCoord = useCallback(() => {
    if (!previewCoord) return;
    const topMatch = suggestions[0];
    const name = topMatch?.text || topMatch?.place_name || value.trim() || formatCoordLabel(previewCoord);
    applyLocation(previewCoord, name);
    setSugg([]);
    setOpen(false);
  }, [previewCoord, suggestions, value, applyLocation]);

  const handleClear = useCallback(() => {
    skipRef.current = true;
    setValue('');
    setSugg([]);
    setOpen(false);
    setPreviewCoord(null);
    setConfirmedCoord(null);
    confirmedNameRef.current = '';
    lastSyncedRef.current = null;
    onSelect(null, '');
  }, [onSelect]);

  const displayCoord = previewCoord ?? confirmedCoord;
  const isPreviewOnly = !confirmedCoord && !!previewCoord;

  return (
    <div className={styles.locationSearchWrap} ref={wrapRef}>
      <div className={styles.locationSearchRow}>
        <span className={styles.locationSearchPin} style={{ background: color }}>
          <span className={styles.endpointIconLetter}>{letter}</span>
        </span>
        <div className={styles.locationSearchInputWrap}>
          <input
            ref={inputRef}
            type="search"
            className={`${styles.locationSearchInput} ${isPreviewOnly ? styles.locationSearchInputPreview : ''}`}
            placeholder={placeholder}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') { setOpen(false); }
              if (e.key === 'Enter') {
                e.preventDefault();
                if (previewCoord && !open) { handleConfirmCoord(); }
                else if (suggestions[0]) { handleSelect(suggestions[0]); }
              }
            }}
            aria-label={placeholder}
            aria-autocomplete="list"
          />
          {loading && <SpinnerDotted size={12} color="#39979d" thickness={160} className={styles.locationSearchSpinner} />}
          {!loading && value && (
            <button
              type="button"
              className={styles.locationSearchClear}
              onClick={handleClear}
              aria-label="Clear"
              tabIndex={0}
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Coordinates shown below the place name field */}
      {displayCoord && !open && (
        <div className={`${styles.locationSearchCoordRow} ${isPreviewOnly ? styles.locationSearchCoordRowPreview : ''}`}>
          <span className={styles.locationSearchCoord}>
            📍 {displayCoord[1].toFixed(5)}°, {displayCoord[0].toFixed(5)}°
          </span>
          {isPreviewOnly && (
            <button
              type="button"
              className={styles.locationSearchConfirmBtn}
              onClick={handleConfirmCoord}
              aria-label="Confirm this location"
              title="Confirm location"
            >
              ✓ Set
            </button>
          )}
        </div>
      )}

      {open && suggestions.length > 0 && (
        <ul className={styles.stopDropdown} role="listbox" aria-label="Location suggestions">
          {suggestions.map((f) => (
            <li key={f.id} role="option" aria-selected={false}>
              <button type="button" className={styles.stopDropdownItem} onClick={() => handleSelect(f)}>
                <span className={styles.stopDropdownMain}>{f.text}</span>
                <span className={styles.stopDropdownSub}>{f.place_name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ─── Single stop search row ────────────────────────────────────────────────
const StopSearch = ({ index, total, label, coord, onUpdateCoord, onFlyTo }) => {
  const [value, setValue]         = useState(label || '');
  const [suggestions, setSugg]    = useState([]);
  const [open, setOpen]           = useState(false);
  const [loading, setLoading]     = useState(false);
  const wrapRef                   = useRef(null);
  const debounceRef               = useRef(null);
  const skipRef                   = useRef(false);

  // Keep value in sync when label changes externally
  useEffect(() => { setValue(label || ''); }, [label]);

  // Close on outside click
  useEffect(() => {
    const handle = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  useEffect(() => {
    if (skipRef.current) { skipRef.current = false; return undefined; }
    const trimmed = value.trim();
    if (trimmed.length < 2) { setSugg([]); setOpen(false); return undefined; }

    const parsed = parseCoordinatePair(trimmed);
    if (parsed) {
      clearTimeout(debounceRef.current);
      const [lng, lat] = parsed;
      const name = formatCoordinates(parsed);
      skipRef.current = true;
      setValue(name);
      setSugg([]);
      setOpen(false);
      onUpdateCoord(index, [lng, lat], name);
      onFlyTo([lng, lat]);
      return undefined;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      const results = await geocode(trimmed, MAPBOX_TOKEN);
      setSugg(results);
      setOpen(results.length > 0);
      setLoading(false);
    }, 320);
    return () => clearTimeout(debounceRef.current);
  }, [value]);

  const handleSelect = useCallback((feature) => {
    const [lng, lat] = feature.center;
    const name = feature.text || feature.place_name || '';
    skipRef.current = true;
    setValue(name);
    setSugg([]);
    setOpen(false);
    onUpdateCoord(index, [lng, lat], name);
    onFlyTo([lng, lat]);
  }, [index, onUpdateCoord, onFlyTo]);

  const isFirst = index === 0;
  const isLast  = index === total - 1;
  const stopNumber = getStopNumber(index);
  const placeholder = isFirst ? 'Start location…' : isLast ? 'Destination…' : `Stop ${stopNumber}…`;

  return (
    <div className={styles.stopSearchWrap} ref={wrapRef}>
      <div className={styles.stopSearchBox}>
        <input
          type="search"
          className={styles.stopSearchInput}
          placeholder={placeholder}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false);
            if (e.key === 'Enter') {
              e.preventDefault();
              const parsed = parseCoordinatePair(value.trim());
              if (parsed) {
                const [lng, lat] = parsed;
                const name = formatCoordinates(parsed);
                skipRef.current = true;
                setValue(name);
                setSugg([]);
                setOpen(false);
                onUpdateCoord(index, [lng, lat], name);
                onFlyTo([lng, lat]);
              } else if (suggestions[0]) {
                handleSelect(suggestions[0]);
              }
            }
          }}
          aria-label={`Location for stop ${stopNumber}`}
          aria-autocomplete="list"
        />
        {loading
          ? <SpinnerDotted size={12} color="#39979d" thickness={160} className={styles.stopSpinner} />
          : coord && <span className={styles.stopCoordPill} title={`${coord[1].toFixed(5)}, ${coord[0].toFixed(5)}`}>📍</span>
        }
      </div>
      {open && suggestions.length > 0 && (
        <ul className={styles.stopDropdown} role="listbox" aria-label="Location suggestions">
          {suggestions.map((f) => (
            <li key={f.id} role="option" aria-selected={false}>
              <button type="button" className={styles.stopDropdownItem} onClick={() => handleSelect(f)}>
                <span className={styles.stopDropdownMain}>{f.text}</span>
                <span className={styles.stopDropdownSub}>{f.place_name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {coord && (
        <p className={styles.stopCoordText}>
          {coord[1].toFixed(5)}°, {coord[0].toFixed(5)}°
        </p>
      )}
    </div>
  );
};

// ─── RouteInfoPanel ────────────────────────────────────────────────────────
const PROFILE_OPTIONS = [
  { value: 'driving', label: '🚗 Driving', desc: 'Roads & highways' },
  { value: 'walking', label: '🚶 Walking', desc: 'Footpaths & roads' },
  { value: 'cycling', label: '🚴 Cycling', desc: 'Cycling-friendly paths' },
];

const RouteInfoPanel = ({
  routeName,
  setRouteName,
  routeDescription,
  setRouteDescription,
  waypoints,
  waypointLabels,
  selectedIndex,
  drawFromIndex,
  totalDistanceKm,
  isSaving,
  isLoadingRoutes,
  savedRoutes,
  editingRouteId,
  generatedCoordinates,
  isGenerating,
  isRouteStale,
  routeProfile,
  setRouteProfile,
  onGenerateRoute,
  onClearGeneratedRoute,
  onSave,
  onClear,
  onLoadRoutes,
  onLoadRoute,
  onDeleteRoute,
  onDeleteWaypoint,
  onSelectWaypoint,
  onSetWaypointLabel,
  onUpdateWaypointCoord,
  onMoveWaypoint,
  onAddStop,
  onFlyTo,
  onSetDrawFrom,
  activeRouteColor,
  canvasRoutes,
  onNewRoute,
  onBranchFromStop,
  onSwitchToRoute,
  onRemoveCanvasRoute,
  onStartMarker,
  onDestMarker,
  startMarker,
  destMarker,
}) => {
  const [deletingId, setDeletingId]     = useState(null);
  const [dragFrom, setDragFrom]         = useState(null);
  const [dragOver, setDragOver]         = useState(null);
  const [addingStop, setAddingStop]     = useState(false);
  const addStopRef                      = useRef(null);

  // ── "Add Stop" geocoding state ──────────────────────────────────────────
  const [addQuery, setAddQuery]       = useState('');
  const [addSugg, setAddSugg]         = useState([]);
  const [addOpen, setAddOpen]         = useState(false);
  const [addLoading, setAddLoading]   = useState(false);
  const addDebounceRef                = useRef(null);

  useEffect(() => {
    if (!addingStop) { setAddQuery(''); setAddSugg([]); setAddOpen(false); return undefined; }
    setTimeout(() => addStopRef.current?.focus(), 50);
    return undefined;
  }, [addingStop]);

  useEffect(() => {
    const trimmed = addQuery.trim();
    if (trimmed.length < 2) { setAddSugg([]); setAddOpen(false); return undefined; }

    const parsed = parseCoordinatePair(trimmed);
    if (parsed) {
      clearTimeout(addDebounceRef.current);
      const [lng, lat] = parsed;
      const name = formatCoordinates(parsed);
      onAddStop([lng, lat], name);
      onFlyTo([lng, lat]);
      setAddingStop(false);
      setAddQuery('');
      setAddSugg([]);
      setAddOpen(false);
      return undefined;
    }

    clearTimeout(addDebounceRef.current);
    addDebounceRef.current = setTimeout(async () => {
      setAddLoading(true);
      const results = await geocode(trimmed, MAPBOX_TOKEN);
      setAddSugg(results);
      setAddOpen(results.length > 0);
      setAddLoading(false);
    }, 320);
    return () => clearTimeout(addDebounceRef.current);
  }, [addQuery]);

  const handleAddStopSelect = (feature) => {
    const [lng, lat] = feature.center;
    const name = feature.text || feature.place_name || '';
    onAddStop([lng, lat], name);
    onFlyTo([lng, lat]);
    setAddingStop(false);
    setAddQuery('');
    setAddSugg([]);
    setAddOpen(false);
  };

  // ── Drag-and-drop reorder ───────────────────────────────────────────────
  const handleDragStart = (e, index) => {
    setDragFrom(index);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOver(index);
  };
  const handleDrop = (e, index) => {
    e.preventDefault();
    if (dragFrom !== null && dragFrom !== index) {
      onMoveWaypoint(dragFrom, index);
    }
    setDragFrom(null);
    setDragOver(null);
  };
  const handleDragEnd = () => { setDragFrom(null); setDragOver(null); };

  // ── Delete saved route (with confirmation) ────────────────────────────
  /** Route object pending deletion confirmation, or null */
  const [confirmDeleteRoute, setConfirmDeleteRoute] = useState(null);

  const handleDeleteClick = (route) => {
    setConfirmDeleteRoute(route);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeleteRoute) return;
    const { id } = confirmDeleteRoute;
    setConfirmDeleteRoute(null);
    setDeletingId(id);
    await onDeleteRoute(id);
    setDeletingId(null);
  };

  const handleCancelDelete = () => setConfirmDeleteRoute(null);

  const canSave = waypoints.length >= 2 && routeName.trim().length > 0;

  // ── Location finder markers (independent of route waypoints) ────────────────
  const handleStartMarker = useCallback((coord) => {
    onStartMarker(coord);
    if (coord) onFlyTo(coord);
  }, [onStartMarker, onFlyTo]);

  const handleDestMarker = useCallback((coord) => {
    onDestMarker(coord);
    if (coord) onFlyTo(coord);
  }, [onDestMarker, onFlyTo]);

  return (
    <div className={styles.infoPanel}>

      {/* ── Start & Destination search ────────────────────────────────── */}
      <section className={styles.panelSection}>
        <div className={styles.sectionHeader}>
          <h6 className={styles.sectionTitle}>Route Endpoints</h6>
          <button
            type="button"
            className={styles.newRouteBtn}
            onClick={() => onNewRoute()}
            aria-label="Start a new separate route"
          >
            + New Route
          </button>
        </div>

        <LocationSearch
          color="#22c55e"
          letter="A"
          placeholder="Search start or enter lat, lng…"
          onSelect={handleStartMarker}
          onPreview={handleStartMarker}
          syncCoord={startMarker}
        />

        <div className={styles.endpointsDivider} />

        <LocationSearch
          color="#ef4444"
          letter="B"
          placeholder="Search destination or enter lat, lng…"
          onSelect={handleDestMarker}
          onPreview={handleDestMarker}
          syncCoord={destMarker}
        />
      </section>

      {/* ── Route Details ─────────────────────────────────────────────── */}
      <section className={styles.panelSection}>
        <h6 className={styles.sectionTitle}>Route Details</h6>
        <div className={styles.formGroup}>
          <label className={styles.formLabel} htmlFor="route-name">
            Name <span className={styles.required}>*</span>
          </label>
          <input
            id="route-name"
            type="text"
            className={styles.formInput}
            placeholder="e.g., Atlantic Crossing"
            value={routeName}
            onChange={(e) => setRouteName(e.target.value)}
            maxLength={100}
            aria-label="Route name"
            aria-required="true"
          />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.formLabel} htmlFor="route-desc">Description</label>
          <textarea
            id="route-desc"
            className={styles.formTextarea}
            placeholder="Optional route notes..."
            value={routeDescription}
            onChange={(e) => setRouteDescription(e.target.value)}
            rows={2}
            maxLength={500}
            aria-label="Route description"
          />
        </div>
      </section>

      {/* ── Stats ─────────────────────────────────────────────────────── */}
      <section className={styles.statsRow} aria-label="Route statistics">
        <div className={styles.stat}>
          <span className={styles.statVal}>{waypoints.length}</span>
          <span className={styles.statLbl}>Stops</span>
        </div>
        <div className={styles.statDivider} />
        <div className={styles.stat}>
          <span className={styles.statVal}>{formatDistance(totalDistanceKm)}</span>
          <span className={styles.statLbl}>Distance</span>
        </div>
        <div className={styles.statDivider} />
        <div className={styles.stat}>
          <span className={styles.statVal}>
            {waypoints.length >= 2 ? `${(totalDistanceKm / 1.852).toFixed(1)}` : '—'}
          </span>
          <span className={styles.statLbl}>Nautical mi</span>
        </div>
      </section>


      {/* ── Action Buttons ────────────────────────────────────────────── */}
      <div className={styles.actionRow}>
        <button
          type="button"
          className={styles.saveBtn}
          onClick={onSave}
          disabled={isSaving || !canSave}
          aria-label={editingRouteId ? 'Update route' : 'Save route'}
          title={!canSave ? 'Add at least 2 stops and enter a name' : ''}
        >
          {isSaving
            ? <SpinnerDotted size={16} color="#fff" thickness={160} />
            : editingRouteId ? 'Update Route' : 'Save Route'
          }
        </button>
        <button
          type="button"
          className={styles.clearBtn}
          onClick={onClear}
          disabled={isSaving}
          aria-label="Clear all waypoints and reset form"
        >
          Clear
        </button>
      </div>

      {editingRouteId && (
        <p className={styles.editingBadge}>✏ Editing saved route</p>
      )}

      {/* ── Navigation-style Stops List ───────────────────────────────── */}
      <section className={styles.panelSection}>
        <h6 className={styles.sectionTitle}>
          {waypoints.length === 0 ? 'Stops' : `Stops (${waypoints.length})`}
        </h6>

        {waypoints.length === 0 && (
          <p className={styles.emptyMsg}>
            Click the map to place stops, or use &ldquo;Add Stop&rdquo; to search by name.
          </p>
        )}

        {waypoints.length > 0 && (
          <ol className={styles.stopsList} aria-label="Route stops">
            {waypoints.map((coord, index) => {
              const isFirst  = index === 0;
              const isLast   = index === waypoints.length - 1;
              const stopNumber = getStopNumber(index);
              const isDragging = dragFrom === index;
              const isDropTarget = dragOver === index && dragFrom !== index;

              return (
                <li
                  /* eslint-disable-next-line react/no-array-index-key */
                  key={`stop-${index}`}
                  className={[
                    styles.stopRow,
                    selectedIndex === index ? styles.stopRowSelected : '',
                    isDragging       ? styles.stopRowDragging    : '',
                    isDropTarget     ? styles.stopRowDropTarget  : '',
                  ].filter(Boolean).join(' ')}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e)  => handleDragOver(e, index)}
                  onDrop={(e)      => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  aria-label={`Stop ${stopNumber}`}
                >
                  {/* Connector line above (all but first) */}
                  {!isFirst && <div className={styles.stopConnectorAbove} aria-hidden="true" />}

                  <div className={styles.stopInner}>
                    {/* Drag handle */}
                    <span className={styles.dragHandle} title="Drag to reorder" aria-hidden="true">⠿</span>

                    {/* Letter badge */}
                    <span
                      className={[
                        styles.stopBadge,
                        isFirst ? styles.stopBadgeStart : '',
                        isLast && !isFirst ? styles.stopBadgeEnd : '',
                      ].filter(Boolean).join(' ')}
                      aria-hidden="true"
                    >
                      {stopNumber}
                    </span>

                    {/* Stop content */}
                    <div
                      className={styles.stopContent}
                      onClick={() => onSelectWaypoint(index)}
                      onKeyDown={(e) => e.key === 'Enter' && onSelectWaypoint(index)}
                      role="button"
                      tabIndex={0}
                    >
                      <div className={styles.stopFieldRow}>
                        <StopSearch
                          index={index}
                          total={waypoints.length}
                          label={waypointLabels[index] || ''}
                          coord={coord}
                          onUpdateCoord={onUpdateWaypointCoord}
                          onFlyTo={onFlyTo}
                        />

                        <div className={styles.stopActions}>
                          <button
                            type="button"
                            className={`${styles.drawFromBtn} ${drawFromIndex === index ? styles.drawFromBtnActive : ''}`}
                            onClick={(e) => { e.stopPropagation(); onSetDrawFrom(index); }}
                            aria-pressed={drawFromIndex === index}
                            aria-label={drawFromIndex === index ? `Cancel draw from stop ${stopNumber}` : `Draw route from stop ${stopNumber}`}
                            title={drawFromIndex === index ? 'Cancel — click to stop drawing from here' : 'Draw route from this stop'}
                          >
                            {drawFromIndex === index ? '✏' : '→'}
                          </button>

                          <button
                            type="button"
                            className={styles.branchRouteBtn}
                            onClick={(e) => { e.stopPropagation(); onBranchFromStop(index); }}
                            aria-label={`Start a new separate route from stop ${stopNumber}`}
                            title={`Branch: start a new independent route from stop ${stopNumber}`}
                          >
                            ⑂
                          </button>

                          <button
                            type="button"
                            className={styles.stopDeleteBtn}
                            onClick={(e) => { e.stopPropagation(); onDeleteWaypoint(index); }}
                            aria-label={`Remove stop ${stopNumber}`}
                            title="Remove stop"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Connector line below (all but last) */}
                  {!isLast && <div className={styles.stopConnectorBelow} aria-hidden="true" />}
                </li>
              );
            })}
          </ol>
        )}

        {/* Add Stop */}
        <div className={styles.addStopArea}>
          {!addingStop ? (
            <button
              type="button"
              className={styles.addStopBtn}
              onClick={() => setAddingStop(true)}
              aria-label="Add a new stop to the route"
            >
              <span className={styles.addStopPlus}>+</span>
              Add Stop
            </button>
          ) : (
            <div className={styles.addStopSearch}>
              <div className={styles.stopSearchBox}>
                <input
                  ref={addStopRef}
                  type="search"
                  className={styles.stopSearchInput}
                  placeholder="Search for a location…"
                  value={addQuery}
                  onChange={(e) => setAddQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setAddingStop(false);
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const parsed = parseCoordinatePair(addQuery.trim());
                      if (parsed) {
                        const [lng, lat] = parsed;
                        const name = formatCoordinates(parsed);
                        onAddStop([lng, lat], name);
                        onFlyTo([lng, lat]);
                        setAddingStop(false);
                        setAddQuery('');
                        setAddSugg([]);
                        setAddOpen(false);
                      } else if (addSugg[0]) {
                        handleAddStopSelect(addSugg[0]);
                      }
                    }
                  }}
                  aria-label="Search location to add as stop"
                />
                {addLoading && <SpinnerDotted size={12} color="#39979d" thickness={160} />}
                <button
                  type="button"
                  className={styles.stopSearchCancel}
                  onClick={() => setAddingStop(false)}
                  aria-label="Cancel add stop"
                >✕</button>
              </div>
              {addOpen && addSugg.length > 0 && (
                <ul className={styles.stopDropdown} role="listbox" aria-label="Location suggestions">
                  {addSugg.map((f) => (
                    <li key={f.id} role="option" aria-selected={false}>
                      <button type="button" className={styles.stopDropdownItem} onClick={() => handleAddStopSelect(f)}>
                        <span className={styles.stopDropdownMain}>{f.text}</span>
                        <span className={styles.stopDropdownSub}>{f.place_name}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <p className={styles.addStopHint}>
                Or click anywhere on the map to place the stop.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* ── Saved Routes ──────────────────────────────────────────────── */}
      <section className={styles.panelSection}>
        <div className={styles.sectionHeader}>
          <h6 className={styles.sectionTitle}>Saved Routes</h6>
          <button
            type="button"
            className={styles.refreshBtn}
            onClick={onLoadRoutes}
            disabled={isLoadingRoutes}
            aria-label="Refresh saved routes"
          >
            {isLoadingRoutes
              ? <SpinnerDotted size={13} color="#39979d" thickness={160} />
              : '↻ Refresh'
            }
          </button>
        </div>

        {savedRoutes.length === 0 ? (
          <p className={styles.emptyMsg}>
            {isLoadingRoutes ? 'Loading...' : 'No saved routes. Click Refresh to load.'}
          </p>
        ) : (
          <ul className={styles.savedRoutesList} role="list" aria-label="Saved routes">
            {savedRoutes.map((route) => {
              // Normalise id defensively — toJSON plugin uses `id`, raw Mongo uses `_id`
              const routeId = route.id || route._id;
              const isEditing  = editingRouteId === routeId;
              const isDeleting = deletingId === routeId;
              return (
              <li
                key={routeId}
                className={`${styles.savedRouteItem} ${isEditing ? styles.savedRouteItemActive : ''}`}
              >
                <div className={styles.savedRouteInfo}>
                  <span className={styles.savedRouteName}>{route.name}</span>
                  {route.description && (
                    <span className={styles.savedRouteDesc}>{route.description}</span>
                  )}
                  <span className={styles.savedRouteMeta}>
                    {route.geoJson?.geometry?.coordinates?.length || 0} stops
                  </span>
                </div>
                <div className={styles.savedRouteActions}>
                  <button
                    type="button"
                    className={`${styles.loadRouteBtn} ${isEditing ? styles.loadRouteBtnActive : ''}`}
                    onClick={() => onLoadRoute({ ...route, id: routeId })}
                    aria-label={`Load and edit route: ${route.name}`}
                    title={isEditing ? 'Currently editing' : 'Load for editing'}
                  >
                    {isEditing ? 'Editing' : 'Edit'}
                  </button>
                  <button
                    type="button"
                    className={styles.deleteRouteBtn}
                    onClick={() => handleDeleteClick({ ...route, id: routeId })}
                    disabled={isDeleting || !routeId}
                    aria-label={`Delete route: ${route.name}`}
                    title={routeId ? 'Delete route' : 'Cannot delete — invalid ID'}
                  >
                    {isDeleting
                      ? <SpinnerDotted size={12} color="#ef4444" thickness={160} />
                      : '✕'
                    }
                  </button>
                </div>
              </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* ── Delete confirmation modal ──────────────────────────────────── */}
      {confirmDeleteRoute && (
        <div
          className={styles.confirmOverlay}
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-delete-title"
          aria-describedby="confirm-delete-desc"
        >
          <div className={styles.confirmModal}>
            <div className={styles.confirmIcon} aria-hidden="true">🗑</div>
            <h6 id="confirm-delete-title" className={styles.confirmTitle}>
              Delete Route?
            </h6>
            <p id="confirm-delete-desc" className={styles.confirmDesc}>
              <strong>{confirmDeleteRoute.name}</strong> will be permanently
              deleted and cannot be recovered.
            </p>
            <div className={styles.confirmActions}>
              <button
                type="button"
                className={styles.confirmCancelBtn}
                onClick={handleCancelDelete}
                autoFocus
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.confirmDeleteBtn}
                onClick={handleConfirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteInfoPanel;
